import React, { useState, useEffect } from "react";
import { FiInfo } from "react-icons/fi";
import axios from "../axiosConfig";

// Components
import DocumentList from "../components/Doc_Repo/DocumentList";
import SearchBox from "../components/General/SearchBox";
import QueryGuideModal from "../components/Doc_Repo/QueryGuideModel";

const DocumentRepository = ({ accessToken }) => {
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [activeSearchTerms, setActiveSearchTerms] = useState([]);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [promptMessage, setPromptMessage] = useState(null);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const extractKeywordsFromQuery = (query) => {
    if (!query || typeof query !== "string") return [];
    const matches = query.trim().match(/([^\s&/!()]+)/g) || [];
    return [...new Set(matches)].filter(
      (kw) => !["AND", "OR", "NOT"].includes(kw.toUpperCase())
    );
  };

  const hasNonSpecialKeyword = (query) => {
    if (!query || typeof query !== "string") return false;
    // remove only these explicit symbols: &, /, !, (, ), ", |
    const stripped = query.replace(/[&\/!\(\)"|]/g, "").trim();
    return stripped.length > 0;
  };

  const handleSearchButtonClick = async () => {
    if (!hasNonSpecialKeyword(searchQuery)) {
      setPromptMessage("Please enter a prompt");
      return;
    }

    try {
      setPromptMessage(null);
      const response = await axios.post(
        `/search`,
        {
          query: searchQuery,
          mode: searchMode,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      setDocuments(response.data.results);
      if (searchQuery.trim()) {
        setActiveSearchTerms(extractKeywordsFromQuery(searchQuery));
      }
    } catch (err) {
      setError(err);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setActiveSearchTerms([]);
    fetchDocuments();
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.post(
        `/documents?num=15`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      setDocuments(response.data.results);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchDocuments();
    }
    // eslint-disable-next-line
  }, [accessToken]);

  if (error) {
    return (
      <div className="password-input rounded-xl flex flex-col items-center justify-center h-screen">
        Error fetching documents: {error.message}
      </div>
    );
  }

  return (
    <div>
      <div className="sticky top-[40px] bg-gray-100 shadow-lg p-3 z-10 mb-7">
        <div className="flex justify-center items-center">
          <h1 className="font-extrabold text-3xl text-center flex-grow">
            Document Repository
          </h1>
          <button
            onClick={() => setIsCardOpen(!isCardOpen)}
            className="p-2 bg-gray-300 text-black rounded-lg focus:outline-none transition-all ease-in-out duration-300 hover:bg-gray-400"
          >
            {isCardOpen ? "▲" : "▼"}
          </button>
        </div>

        {isCardOpen && (
          <div className="w-full mt-3 p-6 bg-white shadow-xl rounded-2xl transition-all ease-in-out duration-500">
            <SearchBox searchQuery={searchQuery} handleSearch={handleSearch} mode={1} />
            {promptMessage && (
              <div className="mt-3 text-sm text-red-600">{promptMessage}</div>
            )}
            <div className="mt-4 flex items-center justify-end">
              <div className="mr-3 text-lg">
                <input
                  type="checkbox"
                  checked={searchMode}
                  onChange={(e) => setSearchMode(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-blue-100 border-blue-300 rounded-sm"
                />
                <label className="ml-1 text-lg font-medium text-gray-900">
                  Search Across Strings
                </label>
              </div>
              <button
                onClick={() => setIsGuideOpen(true)}
                className="p-2 bg-gray-200 text-gray-700 rounded-lg mr-2 hover:bg-gray-300"
                title="Search Query Guide"
              >
                <FiInfo size={20} />
              </button>
              <button
                onClick={handleSearchButtonClick}
                className="p-2 bg-blue-500 text-white rounded-lg mr-2"
              >
                Search
              </button>
              <button
                    onClick={handleClearSearch}
                    className="p-2 bg-gray-400 text-white rounded-lg"
                  >
                    Clear
              </button>
            </div>
          </div>
        )}
      </div>

      <QueryGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

      <DocumentList documents={documents} searchTerms={activeSearchTerms} />
      <div className="font-normal text-xl text-center flex-grow">
        Older Entries Are Hidden..
      </div>
    </div>
  );
};

export default DocumentRepository;
